import React, { useState, useEffect } from 'react';
import { 
  CategoryRule, 
  BUSINESS_CATEGORIES, 
  EXPENSE_COGS_TYPES,
  RuleMatchType, 
  RULE_MATCH_TYPE_LABELS 
} from '../types';
import { merchantMappings } from '../utils/csvUtils';

interface CategoryRulesProps {
  rules: CategoryRule[];
  onUpdateRules: (rules: CategoryRule[]) => void;
}

const CategoryRules: React.FC<CategoryRulesProps> = ({ rules, onUpdateRules }) => {
  const [newPattern, setNewPattern] = useState('');
  const [newCategory, setNewCategory] = useState(BUSINESS_CATEGORIES[0]);
  const [newExpenseCOGS, setNewExpenseCOGS] = useState(EXPENSE_COGS_TYPES[0]);
  const [newMatchType, setNewMatchType] = useState<RuleMatchType>(RuleMatchType.CONTAINS);
  const [newCaseSensitive, setNewCaseSensitive] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'pattern' | 'category' | 'expenseCOGS'>('pattern');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [merchantSearchTerm, setMerchantSearchTerm] = useState('');
  const [matchingMerchants, setMatchingMerchants] = useState<string[]>([]);

  // Filter and sort rules based on search term and sort options
  const filteredRules = rules
    .filter(rule => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        rule.pattern.toLowerCase().includes(searchLower) ||
        rule.category.toLowerCase().includes(searchLower) ||
        (rule.expenseCOGS && rule.expenseCOGS.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      // Handle sorting based on sort field and direction
      let comparison = 0;
      
      if (sortField === 'pattern') {
        comparison = a.pattern.localeCompare(b.pattern);
      } else if (sortField === 'category') {
        comparison = a.category.localeCompare(b.category);
      } else if (sortField === 'expenseCOGS') {
        const aValue = a.expenseCOGS || '';
        const bValue = b.expenseCOGS || '';
        comparison = aValue.localeCompare(bValue);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  // Search merchants when merchantSearchTerm changes
  useEffect(() => {
    if (!merchantSearchTerm.trim()) {
      setMatchingMerchants([]);
      return;
    }
    
    const term = merchantSearchTerm.trim().toUpperCase();
    const matches = Object.keys(merchantMappings)
      .filter(merchant => merchant.includes(term))
      .slice(0, 10); // Limit to 10 results
    
    setMatchingMerchants(matches);
  }, [merchantSearchTerm]);

  const handleAddRule = () => {
    if (newPattern.trim() === '') return;
    
    const newRule: CategoryRule = { 
      pattern: newPattern, 
      category: newCategory,
      expenseCOGS: newExpenseCOGS,
      matchType: newMatchType,
      caseSensitive: newCaseSensitive
    };
    
    const updatedRules = [...rules, newRule];
    onUpdateRules(updatedRules);
    
    // Reset form
    setNewPattern('');
    setNewCategory(BUSINESS_CATEGORIES[0]);
    setNewExpenseCOGS(EXPENSE_COGS_TYPES[0]);
    setNewMatchType(RuleMatchType.CONTAINS);
    setNewCaseSensitive(false);
  };

  const handleRemoveRule = (index: number) => {
    const updatedRules = [...rules];
    updatedRules.splice(index, 1);
    onUpdateRules(updatedRules);
  };

  const handleEditRule = (index: number) => {
    const rule = rules[index];
    setEditIndex(index);
    setNewPattern(rule.pattern);
    setNewCategory(rule.category);
    setNewExpenseCOGS(rule.expenseCOGS || EXPENSE_COGS_TYPES[0]);
    setNewMatchType(rule.matchType || RuleMatchType.CONTAINS);
    setNewCaseSensitive(rule.caseSensitive || false);
    setIsEditing(true);
    setIsAdvancedMode((rule.matchType !== RuleMatchType.CONTAINS) || Boolean(rule.caseSensitive));
  };

  const handleUpdateRule = () => {
    if (editIndex === null || newPattern.trim() === '') return;
    
    const updatedRule: CategoryRule = {
      pattern: newPattern,
      category: newCategory,
      expenseCOGS: newExpenseCOGS,
      matchType: newMatchType,
      caseSensitive: newCaseSensitive
    };
    
    const updatedRules = [...rules];
    updatedRules[editIndex] = updatedRule;
    onUpdateRules(updatedRules);
    
    // Reset form
    setNewPattern('');
    setNewCategory(BUSINESS_CATEGORIES[0]);
    setNewExpenseCOGS(EXPENSE_COGS_TYPES[0]);
    setNewMatchType(RuleMatchType.CONTAINS);
    setNewCaseSensitive(false);
    setEditIndex(null);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setNewPattern('');
    setNewCategory(BUSINESS_CATEGORIES[0]);
    setNewExpenseCOGS(EXPENSE_COGS_TYPES[0]);
    setNewMatchType(RuleMatchType.CONTAINS);
    setNewCaseSensitive(false);
    setEditIndex(null);
    setIsEditing(false);
    setIsAdvancedMode(false);
  };

  const getRuleDescription = (rule: CategoryRule): string => {
    const matchType = rule.matchType || RuleMatchType.CONTAINS;
    const caseSensitive = rule.caseSensitive ? 'case sensitive' : 'case insensitive';
    const matchTypeLabel = RULE_MATCH_TYPE_LABELS[matchType];
    
    return `${matchTypeLabel} match for "${rule.pattern}" (${caseSensitive})`;
  };

  const handleSelectMerchant = (merchant: string) => {
    setNewPattern(merchant);
    setMerchantSearchTerm('');
    
    // If merchant exists in mappings, auto-fill category and expenseCOGS
    if (merchantMappings[merchant]) {
      const mapping = merchantMappings[merchant];
      setNewCategory(mapping.category);
      setNewExpenseCOGS(mapping.expenseCOGS);
    }
  };

  const handleSort = (field: 'pattern' | 'category' | 'expenseCOGS') => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and reset to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleImportRules = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const importedRules = JSON.parse(event.target?.result as string) as CategoryRule[];
          if (Array.isArray(importedRules)) {
            onUpdateRules(importedRules);
          }
        } catch (error) {
          console.error('Error importing rules:', error);
          alert('Invalid rules file. Please try again.');
        }
      };
      reader.readAsText(file);
    };
    
    input.click();
  };

  const handleExportRules = () => {
    const dataStr = JSON.stringify(rules, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
    
    const exportName = `categorization-rules-${new Date().toISOString().split('T')[0]}.json`;
    
    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', exportName);
    link.click();
  };

  const handleCreateFromMerchantMappings = () => {
    const mappingRules = Object.entries(merchantMappings).map(([merchant, mapping]) => ({
      pattern: merchant,
      category: mapping.category,
      expenseCOGS: mapping.expenseCOGS,
      matchType: RuleMatchType.CONTAINS,
      caseSensitive: false
    }));
    
    // Confirm with user as this will replace current rules
    if (window.confirm(`This will create ${mappingRules.length} rules from merchant mappings. Continue?`)) {
      onUpdateRules(mappingRules);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between space-y-4 sm:space-y-0">
        <h3 className="text-lg font-medium text-gray-900">
          Categorization Rules ({rules.length})
        </h3>
        
        <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
          <button
            type="button"
            onClick={handleExportRules}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Export Rules
          </button>
          
          <button
            type="button"
            onClick={handleImportRules}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Import Rules
          </button>
          
          <button
            type="button"
            onClick={handleCreateFromMerchantMappings}
            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Generate from Merchant List
          </button>
        </div>
      </div>
      
      <div className="mt-4 relative">
        <input
          type="text"
          placeholder="Search rules..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
        />
      </div>
      
      <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('pattern')}
              >
                <div className="flex items-center">
                  Pattern
                  {sortField === 'pattern' && (
                    <span className="ml-1">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('category')}
              >
                <div className="flex items-center">
                  Category
                  {sortField === 'category' && (
                    <span className="ml-1">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('expenseCOGS')}
              >
                <div className="flex items-center">
                  Expense/COGS
                  {sortField === 'expenseCOGS' && (
                    <span className="ml-1">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Match Type
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRules.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                  {searchTerm ? 'No matching rules found' : 'No rules defined yet'}
                </td>
              </tr>
            ) : (
              filteredRules.map((rule, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {rule.pattern}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {rule.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {rule.expenseCOGS || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getRuleDescription(rule)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditRule(rules.indexOf(rule))}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleRemoveRule(rules.indexOf(rule))}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      <div className="mt-10 bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {isEditing ? 'Edit Rule' : 'Add New Rule'}
          </h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>
              Define patterns to automatically categorize expenses based on their descriptions.
            </p>
          </div>
          <form className="mt-5 space-y-5">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="merchant-search" className="block text-sm font-medium text-gray-700">
                  Merchant Search
                </label>
                <div className="mt-1 relative">
                  <input
                    type="text"
                    id="merchant-search"
                    placeholder="Search known merchants..."
                    value={merchantSearchTerm}
                    onChange={(e) => setMerchantSearchTerm(e.target.value)}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                  {matchingMerchants.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto sm:text-sm">
                      {matchingMerchants.map((merchant) => (
                        <div
                          key={merchant}
                          onClick={() => handleSelectMerchant(merchant)}
                          className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                        >
                          {merchant}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Or enter a pattern below
                </p>
              </div>

              <div>
                <label htmlFor="pattern" className="block text-sm font-medium text-gray-700">
                  Pattern
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="pattern"
                    id="pattern"
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter merchant name or pattern"
                    value={newPattern}
                    onChange={(e) => setNewPattern(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                >
                  {BUSINESS_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="expenseCOGS" className="block text-sm font-medium text-gray-700">
                  Expense/COGS Type
                </label>
                <select
                  id="expenseCOGS"
                  name="expenseCOGS"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={newExpenseCOGS}
                  onChange={(e) => setNewExpenseCOGS(e.target.value)}
                >
                  {EXPENSE_COGS_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setIsAdvancedMode(!isAdvancedMode)}
                className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isAdvancedMode ? 'Hide Advanced Options' : 'Show Advanced Options'}
              </button>
            </div>

            {isAdvancedMode && (
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="matchType" className="block text-sm font-medium text-gray-700">
                    Match Type
                  </label>
                  <select
                    id="matchType"
                    name="matchType"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    value={newMatchType}
                    onChange={(e) => setNewMatchType(e.target.value as RuleMatchType)}
                  >
                    {Object.entries(RULE_MATCH_TYPE_LABELS).map(([type, label]) => (
                      <option key={type} value={type}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    id="caseSensitive"
                    name="caseSensitive"
                    type="checkbox"
                    checked={newCaseSensitive}
                    onChange={(e) => setNewCaseSensitive(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="caseSensitive" className="ml-2 block text-sm text-gray-900">
                    Case Sensitive
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="mr-3 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={isEditing ? handleUpdateRule : handleAddRule}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={!newPattern.trim()}
              >
                {isEditing ? 'Update Rule' : 'Add Rule'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CategoryRules; 